import React, { useMemo } from 'react';
import classNames from 'classnames';
import { format } from 'date-fns';
import {
  Box,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { getLocalDate } from '@surfline/web-common';

import ErrorBoundary from 'components/ErrorBoundary';
import ErrorMessage from 'components/ErrorMessage';
import SunlightTimesIcon from 'components/Icons/SunlightTimes';
import useCustomError from 'hooks/useCustomError';
import type { SunlightTimes } from 'types/sunlightTimes';

import styles from './ForecastGraphsSunlightTable.module.scss';

export interface ForecastGraphsSunlightTableProps {
  day: number;
  isColumn?: boolean;
  sunlightTimes?: SunlightTimes;
}

interface SunlightTableWrapperProps extends ForecastGraphsSunlightTableProps {
  isLoading?: boolean;
}

interface SunlightTableProps extends Omit<SunlightTableWrapperProps, 'day' | 'sunlightTimes'> {
  data: {
    firstLight: string | null;
    lastLight: string | null;
    sunrise: string | null;
    sunset: string | null;
  };
}

export const getSunlighTimesData = (day: number, sunlightTimes?: SunlightTimes) => {
  if (!sunlightTimes) {
    return {
      firstLight: null,
      lastLight: null,
      sunrise: null,
      sunset: null,
    };
  }

  const firstLight = {
    utcOffset: sunlightTimes?.days?.[day]?.dawnUTCOffset,
    timestamp: sunlightTimes?.days?.[day]?.dawn,
  };
  const sunrise = {
    utcOffset: sunlightTimes?.days?.[day]?.sunriseUTCOffset,
    timestamp: sunlightTimes?.days?.[day]?.sunrise,
  };
  const sunset = {
    utcOffset: sunlightTimes?.days?.[day]?.sunsetUTCOffset,
    timestamp: sunlightTimes?.days?.[day]?.sunset,
  };
  const lastLight = {
    utcOffset: sunlightTimes?.days?.[day]?.duskUTCOffset,
    timestamp: sunlightTimes?.days?.[day]?.dusk,
  };
  const formatString = "h:mmaaaaa'm'";

  const firstLightDate = firstLight?.timestamp
    ? format(getLocalDate(firstLight?.timestamp, firstLight?.utcOffset), formatString)
    : '';
  const sunriseDate = sunrise?.timestamp
    ? format(getLocalDate(sunrise?.timestamp, sunrise?.utcOffset), formatString)
    : '';
  const sunsetDate = sunset?.timestamp
    ? format(getLocalDate(sunset?.timestamp, sunset?.utcOffset), formatString)
    : '';
  const lastLightDate = lastLight?.timestamp
    ? format(getLocalDate(lastLight?.timestamp, lastLight?.utcOffset), formatString)
    : '';

  return {
    firstLight: firstLightDate,
    lastLight: lastLightDate,
    sunrise: sunriseDate,
    sunset: sunsetDate,
  };
};

const SunlightTable = ({ data, isColumn, isLoading }: SunlightTableProps) => (
  <Box
    className={classNames({
      [styles.sunlightTimes]: true,
      [styles.sunlightTimesColumn]: isColumn,
    })}
    data-testid="sunlight-table"
  >
    <Stack direction={isColumn ? 'column' : 'row'} spacing={isColumn ? 3 : 4} alignItems="center">
      <Table aria-label="Morning times table" className={styles.sunlightTable}>
        <TableBody>
          <TableRow>
            <TableCell className={styles.sunlightTableCell}>
              <SunlightTimesIcon className={styles.tableIcon} type="sunrise" />
            </TableCell>
            <TableCell className={styles.sunlightTableCell}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell className={styles.sunlightTableCell}>
                      <Typography
                        className={classNames(
                          styles.sunlightTableCellText,
                          'sl-sunlight-table-cell-text',
                        )}
                        variant="footnote"
                      >
                        First light
                      </Typography>
                    </TableCell>
                    <TableCell className={styles.sunlightTableCell} align="right">
                      {isLoading ? (
                        <Skeleton
                          animation="wave"
                          className={styles.skeleton}
                          data-testid="sunlight-times-table-loading"
                        />
                      ) : (
                        <Typography
                          className={classNames(
                            styles.sunlightTableCellText,
                            'sl-sunlight-table-cell-text',
                          )}
                          variant="footnote"
                        >
                          {data?.firstLight}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={styles.sunlightTableCell}>
                      <Typography
                        className={classNames(
                          styles.sunlightTableCellText,
                          'sl-sunlight-table-cell-text',
                        )}
                        variant="footnote"
                      >
                        Sunrise
                      </Typography>
                    </TableCell>
                    <TableCell className={styles.sunlightTableCell} align="right">
                      {isLoading ? (
                        <Skeleton
                          animation="wave"
                          className={styles.skeleton}
                          data-testid="sunlight-times-table-loading"
                        />
                      ) : (
                        <Typography
                          className={classNames(
                            styles.sunlightTableCellText,
                            'sl-sunlight-table-cell-text',
                          )}
                          variant="footnote"
                        >
                          {data?.sunrise}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <Table aria-label="Evening times table" className={styles.sunlightTable}>
        <TableBody>
          <TableRow>
            <TableCell className={styles.sunlightTableCell}>
              <SunlightTimesIcon className={styles.tableIcon} type="sunset" />
            </TableCell>
            <TableCell className={styles.sunlightTableCell}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell className={styles.sunlightTableCell}>
                      <Typography
                        className={classNames(
                          styles.sunlightTableCellText,
                          'sl-sunlight-table-cell-text',
                        )}
                        variant="footnote"
                      >
                        Sunset
                      </Typography>
                    </TableCell>
                    <TableCell className={styles.sunlightTableCell} align="right">
                      {isLoading ? (
                        <Skeleton
                          animation="wave"
                          className={styles.skeleton}
                          data-testid="sunlight-times-table-loading"
                        />
                      ) : (
                        <Typography
                          className={classNames(
                            styles.sunlightTableCellText,
                            'sl-sunlight-table-cell-text',
                          )}
                          variant="footnote"
                        >
                          {data?.sunset}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={styles.sunlightTableCell}>
                      <Typography
                        className={classNames(
                          styles.sunlightTableCellText,
                          'sl-sunlight-table-cell-text',
                        )}
                        variant="footnote"
                      >
                        Last light
                      </Typography>
                    </TableCell>
                    <TableCell className={styles.sunlightTableCell} align="right">
                      {isLoading ? (
                        <Skeleton
                          animation="wave"
                          className={styles.skeleton}
                          data-testid="sunlight-times-table-loading"
                        />
                      ) : (
                        <Typography
                          className={classNames(
                            styles.sunlightTableCellText,
                            'sl-sunlight-table-cell-text',
                          )}
                          variant="footnote"
                        >
                          {data?.lastLight}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Stack>
  </Box>
);

const SunlightTableWrapper = ({
  day,
  isColumn,
  isLoading,
  sunlightTimes,
}: SunlightTableWrapperProps) => {
  const data = useMemo(() => getSunlighTimesData(day, sunlightTimes), [day, sunlightTimes]);

  return <SunlightTable data={data} isColumn={isColumn} isLoading={isLoading} />;
};

export const ForecastGraphsSunlightTable = ({
  day,
  isColumn,
  sunlightTimes,
}: ForecastGraphsSunlightTableProps) => {
  const { errorMessage, ErrorIcon, hasError } = useCustomError({ error: sunlightTimes?.error });

  if (!sunlightTimes?.days?.length && !sunlightTimes?.loading) return null;

  return (
    <Box data-testid="forecast-graphs-daylight-times-table">
      <ErrorBoundary
        error={hasError}
        errorBoundaryId="forecast-graphs-sunlight-table"
        render={() => (
          <Box
            data-testid="forecast-graphs-daylight-times-table-error"
            style={isColumn ? { height: 150, position: 'relative' } : {}}
          >
            <ErrorMessage Icon={ErrorIcon} message={errorMessage} />
          </Box>
        )}
      >
        <SunlightTableWrapper
          day={day}
          isColumn={isColumn}
          isLoading={sunlightTimes?.loading}
          sunlightTimes={sunlightTimes}
        />
      </ErrorBoundary>
    </Box>
  );
};

export default React.memo(ForecastGraphsSunlightTable);
